<template>
  <v-dialog :value="value" @input="$emit('input', $event)" persistent max-width="500px" dark>
    <v-card class="glass-card pa-2">
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-bluetooth-connect</v-icon>
        <span class="text-h5 font-weight-bold">Gewichtspin verbinden</span>
      </v-card-title>
      <v-card-subtitle>Suche und verbinde dich mit einem smarten Gewichtspin in der Nähe.</v-card-subtitle>

      <v-tabs v-model="tab" background-color="transparent" centered grow>
        <v-tab>Live-Analyse</v-tab>
        <v-tab>Sticker programmieren</v-tab>
      </v-tabs>

      <v-window v-model="tab">
        <v-window-item :value="0">
          <v-card-text>
            <div v-if="!isConnected" class="text-center">
              <v-btn @click="startScanForPin" :loading="isScanning" color="primary" x-large rounded class="my-4">
                <v-icon left>mdi-bluetooth-magnify</v-icon>
                Suche starten
              </v-btn>
              <v-list v-if="foundDevices.length > 0" class="mt-4" style="background: transparent">
                <v-subheader>Gefundene Geräte:</v-subheader>
                <v-list-item v-for="device in foundDevices" :key="device.id" @click="connectToDevice(device.id)" class="split-item mb-2">
                  <v-list-item-icon><v-icon>mdi-weight-lifter</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ device.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ device.id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <p v-if="isScanning && foundDevices.length === 0" class="grey--text mt-4">Suche nach Geräten...</p>
            </div>

            <div v-if="isConnected" class="live-data-container">
              <div class="calibration-status">
                <v-chip v-if="calibrationState === 'calibrating'" small label color="orange" outlined>
                  <v-progress-circular indeterminate size="12" width="2" class="mr-2"></v-progress-circular>
                  Kalibriere Ausrichtung...
                </v-chip>
                <v-chip v-if="calibrationState === 'calibrated'" small label color="green" outlined>
                  <v-icon left small>mdi-check-circle</v-icon>
                  Ausrichtung erkannt ({{ calibratedAxis.toUpperCase() }})
                </v-chip>
              </div>

              <div class="d-flex justify-space-around align-center text-center mt-2">
                <div>
                  <p class="grey--text text--lighten-1">Wdh.</p>
                  <p class="text-h4 font-weight-bold">{{ currentRepNumber }}</p>
                </div>

                <div>
                  <p class="grey--text text--lighten-1">Gewicht</p>
                  <p class="text-h4 font-weight-bold">{{ activeWeight.toFixed(1) }} <span class="text-h6">kg</span></p>
                </div>

                <div class="velocity-meter-container">
                  <div class="velocity-bar positive" :style="positiveVelocityBarStyle"></div>
                  <div class="velocity-bar negative" :style="negativeVelocityBarStyle"></div>
                </div>

                <div>
                  <p class="grey--text text--lighten-1">Geschw.</p>
                  <p class="text-h4 font-weight-bold">{{ liveSpeed.toFixed(2) }}</p>
                </div>
              </div>

              <div class="text-center mt-3">
                <v-btn small text @click="recalibrate" :disabled="calibrationState === 'calibrating'">
                  <v-icon left>mdi-restart</v-icon>
                  Neu kalibrieren
                </v-btn>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="rep-history-container">
                <div v-if="completedReps.length === 0 && calibrationState === 'calibrated'" class="text-center grey--text py-4">
                  Beginne mit deiner ersten Wiederholung.
                </div>
                <div v-if="calibrationState !== 'calibrated'" class="text-center grey--text py-4">Warte auf Kalibrierung...</div>
                <div v-for="rep in completedReps" :key="rep.repNumber" class="rep-history-item">
                  <div class="rep-info">
                    <span class="rep-number">#{{ rep.repNumber }}</span>
                    <span><strong>Ø Geschw:</strong> {{ rep.avgSpeed.toFixed(2) }} m/s</span>
                    <span><strong>Spitze:</strong> {{ rep.peakVelocity.toFixed(2) }} m/s</span>
                    <span><strong>RoM:</strong> {{ rep.rangeOfMotion.toFixed(0) }} cm</span>
                  </div>
                  <div class="rep-chart">
                    <SpeedChart :velocityData="rep.velocityHistory" />
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-window-item>

        <v-window-item :value="1">
          <v-card-text class="text-center">
            <p class="body-1 my-4">Gib den Text ein, der auf den NFC-Sticker geschrieben werden soll.</p>
            <v-text-field v-model="nfcData.text" label="Text für NFC-Sticker" type="text" dark filled rounded></v-text-field>
            <v-btn @click="writeToNFCTag" color="secondary" x-large rounded>
              <v-icon left>mdi-nfc-tap</v-icon>
              Auf Sticker schreiben
            </v-btn>
            <p v-if="nfcStatus" class="mt-4" :class="nfcStatus.color">{{ nfcStatus.message }}</p>
          </v-card-text>
        </v-window-item>
      </v-window>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text large @click="closeDialog">Schliessen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SpeedChart from "./speedChart.vue"

export default {
  name: "PinConnectorDialog",
  components: {
    SpeedChart,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tab: 0,
      isScanning: false,
      isConnected: false,
      foundDevices: [],
      nfcData: { text: "20kg" },
      nfcStatus: null,
      liveSpeed: 0,
      liveVelocity: 0,
      currentRepNumber: 0,
      activeWeight: 0,
      completedReps: [],

      // Status für die Kalibrierung
      calibrationState: "idle", // 'idle', 'calibrating', 'calibrated'
      calibratedAxis: null,
    }
  },
  computed: {
    positiveVelocityBarStyle() {
      const maxVelocity = 2.5
      const heightPercentage = this.liveVelocity > 0 ? Math.min((this.liveVelocity / maxVelocity) * 100, 100) : 0
      return { height: `${heightPercentage / 2}%` }
    },
    negativeVelocityBarStyle() {
      const maxVelocity = 2.5
      const heightPercentage = this.liveVelocity < 0 ? Math.min((Math.abs(this.liveVelocity) / maxVelocity) * 100, 100) : 0
      return { height: `${heightPercentage / 2}%` }
    },
  },
  methods: {
    recalibrate() {
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("recalibrate")
      }
    },
    handleCalibrationUpdate(event) {
      const data = event.detail
      if (!data) return
      this.calibrationState = data.state
      if (data.state === "calibrated") {
        this.calibratedAxis = data.axis
      }
    },
    closeDialog() {
      this.isConnected = false
      this.completedReps = []
      this.currentRepNumber = 0
      this.liveSpeed = 0
      this.liveVelocity = 0
      this.activeWeight = 0
      this.calibrationState = "idle" // Zurücksetzen
      this.$emit("close")
    },
    startScanForPin() {
      if (window.flutter_inappwebview) {
        this.isScanning = true
        this.foundDevices = []
        window.flutter_inappwebview.callHandler("startScan")
      } else {
        this.isScanning = true
        setTimeout(() => {
          this.isScanning = false
          this.foundDevices = [{ name: "WT002 Demo Pin", id: "SIM-123" }]
        }, 2000)
      }
    },
    connectToDevice(deviceId) {
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("connectToPin", deviceId)
      } else {
        this.handlePinConnected()
      }
    },
    writeToNFCTag() {
      if (window.flutter_inappwebview) {
        this.nfcStatus = { message: "Bitte halte dein iPhone an den Sticker...", color: "blue--text" }
        window.flutter_inappwebview.callHandler("writeTextToNFCTag", this.nfcData.text)
      } else {
        this.nfcStatus = { message: "NFC-Schreiben nur in der App verfügbar.", color: "orange--text" }
      }
    },
    handleDeviceFound(event) {
      this.isScanning = false
      if (!this.foundDevices.some((d) => d.id === event.detail.id)) {
        this.foundDevices.push(event.detail)
      }
    },
    handlePinConnected() {
      this.isConnected = true
      this.completedReps = []
      this.currentRepNumber = 0
      this.activeWeight = 0
      this.calibrationState = "idle" // Zurücksetzen
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("setExerciseID", 1)
      }
    },
    handlePinUpdate(event) {
      const data = event.detail
      if (!data) return

      // Update live data only if calibrated
      if (this.calibrationState === "calibrated") {
        this.liveVelocity = data.velocity || 0
        this.liveSpeed = data.speed || 0
        this.currentRepNumber = data.rep
      }

      if (data.weightKg > 0) {
        this.activeWeight = data.weightKg
      }

      if (data.repVelocityHistory && data.repVelocityHistory.length > 0) {
        const history = data.repVelocityHistory
        const sumOfSpeeds = history.reduce((acc, val) => acc + Math.abs(val), 0)
        const avgSpeed = sumOfSpeeds / history.length || 0

        this.completedReps.unshift({
          repNumber: data.rep,
          avgSpeed: avgSpeed,
          peakVelocity: data.peakVelocity,
          rangeOfMotion: data.rangeOfMotion,
          velocityHistory: history,
        })
      }
    },
    handleNFCWriteSuccess() {
      this.nfcStatus = { message: "Sticker erfolgreich beschrieben!", color: "green--text" }
    },
    handleNFCWriteError(event) {
      this.nfcStatus = { message: `Fehler: ${event.detail.error}`, color: "red--text" }
    },
  },
  created() {
    window.addEventListener("gewichtspinDeviceFound", this.handleDeviceFound)
    window.addEventListener("gewichtspinConnected", this.handlePinConnected)
    window.addEventListener("gewichtspinUpdate", this.handlePinUpdate)
    window.addEventListener("nfcWriteSuccess", this.handleNFCWriteSuccess)
    window.addEventListener("nfcWriteError", this.handleNFCWriteError)
    // Listener for calibration
    window.addEventListener("gewichtspinCalibration", this.handleCalibrationUpdate)
  },
  beforeDestroy() {
    window.removeEventListener("gewichtspinDeviceFound", this.handleDeviceFound)
    window.removeEventListener("gewichtspinConnected", this.handlePinConnected)
    window.removeEventListener("gewichtspinUpdate", this.handlePinUpdate)
    window.removeEventListener("nfcWriteSuccess", this.handleNFCWriteSuccess)
    window.removeEventListener("nfcWriteError", this.handleNFCWriteError)
    // Remove listener for calibration
    window.removeEventListener("gewichtspinCalibration", this.handleCalibrationUpdate)
  },
}
</script>

<style scoped>
.glass-card {
  background: rgba(40, 40, 40, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  backdrop-filter: blur(10px);
  border-radius: 16px !important;
}
.split-item {
  border-radius: 12px !important;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background-color 0.2s;
}
.split-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
.velocity-meter-container {
  width: 20px;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.velocity-bar {
  width: 100%;
  position: absolute;
  left: 0;
  transition: height 0.05s linear;
  border-radius: 10px;
}
.velocity-bar.positive {
  bottom: 50%;
  background-color: #4caf50; /* Grün */
}
.velocity-bar.negative {
  top: 50%;
  background-color: #ff5252; /* Rot */
}

.rep-history-container {
  max-height: 250px;
  overflow-y: auto;
  padding-right: 10px;
}
.rep-history-item {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 5px;
}
.rep-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 15px;
  font-size: 0.8rem;
  width: 150px;
  flex-shrink: 0;
}
.rep-info .rep-number {
  font-size: 1.2rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 5px;
}
.rep-chart {
  flex-grow: 1;
  height: 60px;
}
.calibration-status {
  position: absolute;
  top: 16px;
  right: 16px;
}
</style>
